import { permitCategoryDisplayOrder } from '@contracts/order/fields/PermitFieldDisplayOrder';
import { theme } from '@src/common/styles/Theme';
import type { Order } from '@src/orders/domain/Order';
import type { PermitApplication } from '@src/orders/domain/PermitApplication';
import { categories } from '@src/permitField/Fields';
import { AccountsAndInsuranceCategory } from '@src/permits/AccountsAndInsuranceCategory';
import { AxlesConfigurationCategory } from '@src/permits/AxlesConfigurationCategory';
import { CarrierAndPermitDeliveryCategory } from '@src/permits/CarrierAndPermitDeliveryCategory';
import { HaulingEquipmentCategory } from '@src/permits/HaulingEquipmentCategory';
import { LoadInformationCategory } from '@src/permits/LoadInformationCategory';
import { NotesAndDocumentCategory } from '@src/permits/NotesAndDocumentCategory';
import { PermitCategory } from '@src/permits/PermitCategory';
import { RoutingAndStatesCategory } from '@src/permits/RoutingAndStatesCategory';
import React from 'react';

interface Props {
  isMetric: boolean;
  permitApplication: PermitApplication;
  order: Order;
}

export const Permit = ({ isMetric, permitApplication, order }: Props) => {
  const sections = sortedCategories
    .map((category) => {
      let componentToRender: React.ReactNode = null;

      switch (category) {
        case 'axlesConfiguration':
          componentToRender = (
            <AxlesConfigurationCategory
              isMetric={isMetric}
              permitApplication={permitApplication}
              order={order}
            />
          );
          break;
        case 'carrierAndPermitDelivery':
          componentToRender = (
            <CarrierAndPermitDeliveryCategory
              permitApplication={permitApplication}
              order={order}
            />
          );
          break;
        case 'accountsAndInsurances':
          componentToRender = (
            <AccountsAndInsuranceCategory
              permitApplication={permitApplication}
              order={order}
            />
          );
          break;
        case 'haulingEquipment':
          componentToRender = (
            <HaulingEquipmentCategory
              permitApplication={permitApplication}
              order={order}
            />
          );
          break;
        case 'loadInformation':
          componentToRender = (
            <LoadInformationCategory
              permitApplication={permitApplication}
              order={order}
            />
          );
          break;
        case 'routingAndStates':
          componentToRender = (
            <RoutingAndStatesCategory
              permitApplication={permitApplication}
              order={order}
            />
          );
          break;
        case 'notesAndDocuments':
          componentToRender = (
            <NotesAndDocumentCategory
              permitApplication={permitApplication}
              order={order}
            />
          );
          break;
        default:
          componentToRender = (
            <PermitCategory
              name={category}
              category={category}
              permitApplication={permitApplication}
              order={order}
            />
          );
          break;
      }

      if (componentToRender) {
        return (
          <div
            key={category}
            style={{
              marginBottom: theme.spacing['5'],
            }}
          >
            {componentToRender}
          </div>
        );
      }
      return null;
    })
    .filter(Boolean);

  return (
    <div
      style={{
        columnCount: 2,
        columnGap: theme.spacing['3'],
        width: '100%',
      }}
    >
      {sections}
    </div>
  );
};

const displayOrder: string[] = [...permitCategoryDisplayOrder];

const sortedCategories = [...categories].sort((a, b) => {
  const indexA = displayOrder.indexOf(a);
  const indexB = displayOrder.indexOf(b);

  if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB;
  }

  if (indexA !== -1) {
    return -1;
  }

  if (indexB !== -1) {
    return 1;
  }

  return 0;
});
